import {
  faLayerGroup,
  faCode,
  faFlask,
  faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';
import type { LearningPath, Article } from './types';

// Article imports (one file per article)
import { whatIsABacktest } from './articles/what-is-a-backtest';
import { howBacktestingWorks } from './articles/how-backtesting-works';
import { backtestingMistakes } from './articles/backtesting-mistakes';
import { anatomyOfAStrategy } from './articles/anatomy-of-a-strategy';
import { entryExitRules } from './articles/entry-exit-rules';
import { introductionToQsl } from './articles/introduction-to-qsl';
import { runningYourFirstBacktest } from './articles/running-your-first-backtest';
import { understandingBacktestMetrics } from './articles/understanding-backtest-metrics';
import { performanceMetricsDeepDive } from './articles/performance-metrics-deep-dive';
import { behaviourAnalysisDeepDive } from './articles/behaviour-analysis-deep-dive';
import { tradeDistributionDeepDive } from './articles/trade-distribution-deep-dive';
import { exposureTimelineDeepDive } from './articles/exposure-timeline-deep-dive';
import { regimeCoverageDeepDive } from './articles/regime-coverage-deep-dive';
import { drawdownAnalysisDeepDive } from './articles/drawdown-analysis-deep-dive';
import { drawdownEnvelopeDeepDive } from './articles/drawdown-envelope-deep-dive';
import { parameterOptimization } from './articles/parameter-optimization';
import { walkForwardAnalysis } from './articles/walk-forward-analysis';
import { monteCarloSimulation } from './articles/monte-carlo-simulation';
import { stabilityTesting } from './articles/stability-testing';

// ---------------------------------------------------------------------------
// Learning Paths (categories)
// ---------------------------------------------------------------------------
export const learningPaths: LearningPath[] = [
  {
    id: 'backtesting-fundamentals',
    title: 'Backtesting Fundamentals',
    tagline: 'Avoid the Mistakes That Break Strategies',
    subtitle: 'Core Concepts',
    description:
      'Learn what backtesting is, how it works, and how to avoid the common mistakes that cause trading strategies to fail in live markets.',
    estimatedTime: '30 min',
    level: 'Beginner Path',
    icon: faLayerGroup,
    color: '#9d4edd',
    slug: 'backtesting-fundamentals',
    articles: [
      {
        slug: 'what-is-a-backtest',
        title: 'What Is a Backtest?',
        description:
          'Learn what a backtest is, how it works, and why most traders misuse it. A complete guide to testing trading strategies properly.',
        difficulty: 'beginner',
        readTime: '14 min',
        category: 'backtesting-fundamentals',
        tags: ['backtest', 'basics', 'guide'],
        published: true,
      },
      {
        slug: 'how-backtesting-works',
        title: 'How Backtesting Works',
        description:
          'Learn how backtesting works in trading, from strategy rules and historical data to simulated trades, performance metrics, and common mistakes traders make.',
        difficulty: 'beginner',
        readTime: '16 min',
        category: 'backtesting-fundamentals',
        tags: ['backtest', 'mechanics', 'simulation', 'guide'],
        published: true,
      },
      {
        slug: 'backtesting-mistakes',
        title: 'Backtesting Mistakes That Ruin Strategies',
        description:
          'Look-ahead bias, curve fitting, survivorship bias and other silent killers that make backtests worthless.',
        difficulty: 'beginner',
        readTime: '22 min',
        category: 'backtesting-fundamentals',
        tags: ['backtest', 'pitfalls', 'bias'],
        published: true,
      },
    ],
  },
  {
    id: 'strategy-building',
    title: 'Build Trading Strategies',
    tagline: 'Turn Ideas Into Testable Code',
    subtitle: 'Design & Code',
    description:
      'Design rule-based trading strategies from scratch. Define entries, exits, and position sizing, then turn your ideas into testable code.',
    estimatedTime: '40 min',
    level: 'Beginner Path',
    icon: faCode,
    color: '#3b82f6',
    slug: 'strategy-building',
    articles: [
      {
        slug: 'anatomy-of-a-strategy',
        title: 'Anatomy of a Trading Strategy',
        description:
          'What every systematic strategy actually consists of and where most traders go wrong. Entries, exits, risk management, filters, and position sizing.',
        difficulty: 'beginner',
        readTime: '18 min',
        category: 'strategy-building',
        tags: ['strategy', 'design', 'entries', 'exits', 'risk management', 'filters', 'position sizing'],
        published: true,
      },
      {
        slug: 'entry-exit-rules',
        title: 'Defining Entry and Exit Rules',
        description:
          'How to write clear, testable entry and exit conditions — from plain-language ideas to executable code on the Quanthop platform.',
        difficulty: 'intermediate',
        readTime: '16 min',
        category: 'strategy-building',
        tags: ['strategy', 'rules', 'entries', 'exits', 'qsl', 'code'],
        published: true,
      },
      {
        slug: 'introduction-to-qsl',
        title: 'Introduction to QSL',
        description:
          'Learn QSL basics: the three-function lifecycle, parameters, indicators, and how to turn your trading logic into testable code.',
        difficulty: 'intermediate',
        readTime: '18 min',
        category: 'strategy-building',
        tags: ['qsl', 'code', 'indicators'],
        published: true,
      },
    ],
  },
  {
    id: 'backtesting-optimization',
    title: 'Backtesting & Strategy Optimization',
    tagline: 'Test, Measure, and Optimize Properly',
    subtitle: 'Test & Tune',
    description:
      'Run rigorous backtests, understand what Sharpe ratio and drawdown really mean, and optimize strategy parameters without overfitting to historical data.',
    estimatedTime: '45 min',
    level: 'Intermediate Path',
    icon: faFlask,
    color: '#10b981',
    slug: 'backtesting-optimization',
    articles: [
      {
        slug: 'running-your-first-backtest',
        title: 'Running Your First Backtest',
        description:
          'Step-by-step guide to setting up and running a backtest in Quanthop, from choosing a symbol to reading results.',
        difficulty: 'beginner',
        readTime: '10 min',
        category: 'backtesting-optimization',
        tags: ['backtest', 'tutorial'],
      },
      {
        slug: 'understanding-backtest-metrics',
        title: 'Understanding Backtest Results',
        description:
          'A complete guide to reading backtest results — Metrics, Behaviour profiling, Trade Structure, Drawdown, Monte Carlo, Regimes, and the Viability Score.',
        difficulty: 'intermediate',
        readTime: '22 min',
        category: 'backtesting-optimization',
        tags: ['metrics', 'results', 'behaviour', 'drawdown', 'regimes'],
      },
      {
        slug: 'parameter-optimization',
        title: 'Parameter Optimization Without Overfitting',
        description:
          'Use Walk-Forward Analysis and out-of-sample testing to find robust parameters instead of curve-fitted ones.',
        difficulty: 'advanced',
        readTime: '16 min',
        category: 'backtesting-optimization',
        tags: ['optimization', 'wfa', 'overfitting'],
      },
    ],
  },
  {
    id: 'validation-robustness',
    title: 'Strategy Validation & Robustness',
    tagline: 'Prove Your Edge Before Risking Capital',
    subtitle: 'Prove It Works',
    description:
      'Prove your strategy works before risking real capital. Use Walk-Forward Analysis, Monte Carlo simulation, and stability testing to separate real edge from curve fitting.',
    estimatedTime: '45 min',
    level: 'Advanced Path',
    icon: faShieldHalved,
    color: '#f59e0b',
    slug: 'validation-robustness',
    articles: [
      {
        slug: 'walk-forward-analysis',
        title: 'Walk-Forward Analysis Explained',
        description:
          'The gold standard for strategy validation. Learn how WFA proves your strategy adapts to changing markets.',
        difficulty: 'intermediate',
        readTime: '14 min',
        category: 'validation-robustness',
        tags: ['wfa', 'validation'],
      },
      {
        slug: 'monte-carlo-simulation',
        title: 'Monte Carlo Simulation for Traders',
        description:
          'Use randomized trade reordering to understand the range of outcomes your strategy might produce.',
        difficulty: 'advanced',
        readTime: '12 min',
        category: 'validation-robustness',
        tags: ['monte-carlo', 'risk'],
      },
      {
        slug: 'stability-testing',
        title: 'Stability Testing and Parameter Sensitivity',
        description:
          'Test whether small parameter changes break your strategy, or if its edge is robust across nearby values.',
        difficulty: 'advanced',
        readTime: '14 min',
        category: 'validation-robustness',
        tags: ['stability', 'sensitivity', 'robustness'],
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Full article content (keyed by slug, imported from individual files)
// ---------------------------------------------------------------------------
export const articles: Record<string, Article> = {
  'what-is-a-backtest': whatIsABacktest,
  'how-backtesting-works': howBacktestingWorks,
  'backtesting-mistakes': backtestingMistakes,
  'anatomy-of-a-strategy': anatomyOfAStrategy,
  'entry-exit-rules': entryExitRules,
  'introduction-to-qsl': introductionToQsl,
  'running-your-first-backtest': runningYourFirstBacktest,
  'understanding-backtest-metrics': understandingBacktestMetrics,
  'performance-metrics-deep-dive': performanceMetricsDeepDive,
  'behaviour-analysis-deep-dive': behaviourAnalysisDeepDive,
  'trade-distribution-deep-dive': tradeDistributionDeepDive,
  'exposure-timeline-deep-dive': exposureTimelineDeepDive,
  'regime-coverage-deep-dive': regimeCoverageDeepDive,
  'drawdown-analysis-deep-dive': drawdownAnalysisDeepDive,
  'drawdown-envelope-deep-dive': drawdownEnvelopeDeepDive,
  'parameter-optimization': parameterOptimization,
  'walk-forward-analysis': walkForwardAnalysis,
  'monte-carlo-simulation': monteCarloSimulation,
  'stability-testing': stabilityTesting,
};

// ---------------------------------------------------------------------------
// Popular Topics (SEO entry points)
// ---------------------------------------------------------------------------
export const popularTopicGroups = [
  {
    label: 'Start Here',
    topics: [
      { title: 'What is a Backtest?', slug: 'what-is-a-backtest', category: 'backtesting-fundamentals' },
      { title: 'How to Build a Trading Strategy', slug: 'anatomy-of-a-strategy', category: 'strategy-building' },
    ],
  },
  {
    label: 'Common Mistakes',
    topics: [
      { title: 'What is Overfitting in Trading?', slug: 'backtesting-mistakes', category: 'backtesting-fundamentals' },
      { title: 'Why Strategies Fail in Live Markets', slug: 'parameter-optimization', category: 'backtesting-optimization' },
    ],
  },
  {
    label: 'Advanced Concepts',
    topics: [
      { title: 'Walk-Forward Analysis Explained', slug: 'walk-forward-analysis', category: 'validation-robustness' },
      { title: 'Understanding Backtest Results', slug: 'understanding-backtest-metrics', category: 'backtesting-optimization' },
    ],
  },
];

// ---------------------------------------------------------------------------
// Learn by Doing (real example teasers)
// ---------------------------------------------------------------------------
export const learnByDoing = [
  { step: 'Build', title: 'Build a moving average crossover strategy', slug: 'entry-exit-rules', category: 'strategy-building' },
  { step: 'Test', title: 'Run a full backtest and read the results', slug: 'running-your-first-backtest', category: 'backtesting-optimization' },
  { step: 'Validate', title: 'Validate it with Walk-Forward Analysis', slug: 'walk-forward-analysis', category: 'validation-robustness' },
];

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------
export function getPathBySlug(slug: string): LearningPath | undefined {
  return learningPaths.find((p) => p.slug === slug);
}

export function getArticle(slug: string): Article | undefined {
  return articles[slug];
}

export function isArticlePublished(slug: string): boolean {
  const article = articles[slug];
  return !!article?.published;
}

export function getArticlesForPath(pathSlug: string): Article[] {
  const path = getPathBySlug(pathSlug);
  if (!path) return [];
  return path.articles
    .map((a) => articles[a.slug])
    .filter((a): a is Article => !!a);
}

export function getRelatedArticles(articleSlug: string): Article[] {
  const article = articles[articleSlug];
  if (!article) return [];
  return article.relatedSlugs
    .map((s) => articles[s])
    .filter((a): a is Article => !!a && a.published);
}
